import React, { useState, useEffect } from 'react';
import AdminPageTitle from '../components/AdminPageTitle';
import { fetchMessages, fetchMessage, replyToMessage } from '../services/messages';
import './Messages.css';

const Messages = () => {
  const [messages, setMessages] = useState([]);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [replyBody, setReplyBody] = useState('');
  const [sendingReply, setSendingReply] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const perPage = 15;

  const loadMessages = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchMessages({
        page: currentPage,
        per_page: perPage,
        status: filterStatus !== 'all' ? filterStatus : undefined,
      });
      setMessages(data.data || []);
      setTotal(data.total ?? 0);
      setTotalPages(data.last_page ?? 1);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to load messages.');
      setMessages([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMessages();
  }, [currentPage, filterStatus]);

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleString();
  };

  const handleOpenMessage = async (msg) => {
    try {
      setDetailLoading(true);
      setSelectedMessage(null);
      setReplyBody('');
      const full = await fetchMessage(msg.id);
      setSelectedMessage(full);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load message.');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleSendReply = async (e) => {
    e.preventDefault();
    if (!selectedMessage || !replyBody.trim()) return;
    setSendingReply(true);
    setSuccess(null);
    setError(null);
    try {
      await replyToMessage(selectedMessage.id, replyBody.trim());
      setSuccess('Reply sent successfully.');
      setReplyBody('');
      setSelectedMessage((prev) => prev ? { ...prev, replied_at: new Date().toISOString() } : null);
      loadMessages();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send reply.');
    } finally {
      setSendingReply(false);
    }
  };

  return (
    <div className="admin-messages-page">
      <AdminPageTitle title="Messages" />
      <div className="page-header">
        <h1>Messages</h1>
      </div>

      {error && (
        <div className="messages-alert messages-alert-error">{error}</div>
      )}
      {success && (
        <div className="messages-alert messages-alert-success">{success}</div>
      )}

      <div className="messages-filters">
        <label>
          Status:
          <select
            value={filterStatus}
            onChange={(e) => {
              setFilterStatus(e.target.value);
              setCurrentPage(1);
            }}
            className="filter-select"
          >
            <option value="all">All</option>
            <option value="unread">Unread</option>
            <option value="read">Read</option>
          </select>
        </label>
      </div>

      {loading ? (
        <div className="loading">Loading messages...</div>
      ) : (
        <>
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>From</th>
                  <th>Email</th>
                  <th>Subject</th>
                  <th>Status</th>
                  <th>Date</th>
                  <th>Replied</th>
                </tr>
              </thead>
              <tbody>
                {messages.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="no-data">No messages found.</td>
                  </tr>
                ) : (
                  messages.map((msg) => (
                    <tr
                      key={msg.id}
                      className={msg.status === 'unread' ? 'message-row-unread' : ''}
                      onClick={() => handleOpenMessage(msg)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td>{msg.name}</td>
                      <td>{msg.email}</td>
                      <td>{msg.subject}</td>
                      <td>
                        <span className={`message-status message-status-${msg.status}`}>
                          {msg.status}
                        </span>
                      </td>
                      <td>{formatDate(msg.created_at)}</td>
                      <td>{msg.replied_at ? formatDate(msg.replied_at) : '—'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="pagination">
              <button
                className="pagination-btn"
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                Previous
              </button>
              <span className="pagination-info">
                Page {currentPage} of {totalPages}
              </span>
              <button
                className="pagination-btn"
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      {detailLoading && (
        <div className="modal-overlay">
          <div className="message-modal"><div className="loading">Loading...</div></div>
        </div>
      )}

      {selectedMessage && !detailLoading && (
        <div className="modal-overlay" onClick={() => setSelectedMessage(null)}>
          <div className="message-modal" onClick={(e) => e.stopPropagation()}>
            <div className="message-modal-header">
              <h2>{selectedMessage.subject}</h2>
              <button
                type="button"
                className="message-modal-close"
                onClick={() => setSelectedMessage(null)}
              >
                ×
              </button>
            </div>
            <div className="message-modal-body">
              <div className="message-meta">
                <p><strong>From:</strong> {selectedMessage.name} &lt;{selectedMessage.email}&gt;</p>
                {selectedMessage.phone && (
                  <p><strong>Phone:</strong> {selectedMessage.phone}</p>
                )}
                <p><strong>Date:</strong> {formatDate(selectedMessage.created_at)}</p>
              </div>
              <div className="message-content">
                <pre className="message-text">{selectedMessage.message}</pre>
              </div>
              <form onSubmit={handleSendReply} className="message-reply-form">
                <label htmlFor="reply-body">Reply (email will be sent to {selectedMessage.email})</label>
                <textarea
                  id="reply-body"
                  value={replyBody}
                  onChange={(e) => setReplyBody(e.target.value)}
                  rows={6}
                  placeholder="Type your reply..."
                  disabled={sendingReply}
                />
                <div className="message-reply-actions">
                  <button type="submit" className="btn-primary" disabled={sendingReply || !replyBody.trim()}>
                    {sendingReply ? 'Sending...' : 'Send Reply'}
                  </button>
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => setSelectedMessage(null)}
                  >
                    Close
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Messages;
